import { TemplateKey } from "PFApp/constants/templates";
import NotFoundComponent from "PFComponents/not_found/not_found";
import { PageWrapper } from "PFComponents/page_wrapper";
import { isSubtemplate } from "PFCore/helpers/templates";
import { Activity, Id } from "PFCore/types";
import { useTranslation } from "react-i18next";

import { ActivityForm } from "./activity_form";
import { ActivityFormProvider } from "./activity_form_provider";
import { ActivityFormStoreProvider } from "./activity_form_store";
import { ActivityFormState } from "./activity_form_store/activity_form_store.types";
import { useAsyncStoreInitializer } from "./hooks/use_async_store_initializer";
import { EnsureRoleEditPermission } from "./permission/ensure_role_edit_permission";

export const ActivityEditPageContent = ({ initialState }: ActivityEditPageContentProps) => (
  <ActivityFormStoreProvider initialState={initialState}>
    <ActivityFormProvider>
      <EnsureRoleEditPermission>
        <ActivityForm />
      </EnsureRoleEditPermission>
    </ActivityFormProvider>
  </ActivityFormStoreProvider>
);

type ActivityEditPageProps = {
  id?: Id;
  type?: TemplateKey;
  attributes?: Partial<Activity>;
  isClone?: boolean | undefined;
};

export const ActivityFormPage = ({ id, type, attributes, isClone }: ActivityEditPageProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "form" });
  const {
    loaded,
    template,
    activity,
    activityStatus,
    activityCreator,
    errors,
    parentActivity,
    subtemplates,
    attachments
  } = useAsyncStoreInitializer({
    attributes,
    isClone,
    id,
    type
  });

  const isSubtemplateMissing =
    !!activity && !!activity.subtemplate_key && !isSubtemplate(template, activity.subtemplate_key);

  const isNew = !id;
  const templateError = !template && (isNew || !!activity) && t("errors.missingTemplate");
  const subtemplateError = isSubtemplateMissing && t("errors.templateNotSupported");
  const isError = !!errors?.length || !!templateError || !!subtemplateError;

  return (
    <PageWrapper
      isLoading={!loaded}
      isError={isError}
      errorComponent={
        <NotFoundComponent
          message={templateError || subtemplateError || t("errors.activityCouldntLoad")}
          buttonMessage={t("errors.pleaseTryLater")}
          buttonUrl="/"
        />
      }
    >
      <ActivityEditPageContent
        initialState={{
          template,
          activity,
          activityStatus,
          activityCreator,
          parentActivity,
          subtemplates,
          attachments
        }}
      />
    </PageWrapper>
  );
};

type ActivityEditPageContentProps = {
  initialState: Pick<
    ActivityFormState,
    | "template"
    | "activity"
    | "activityStatus"
    | "activityCreator"
    | "parentActivity"
    | "subtemplates"
    | "attachments"
  >;
};
